"use client";

import { Box, Typography } from "@mui/material";
import { generateMediaSrc } from "@v2/components/MediaV2";
import parse, { DOMNode, Element as HtmlElement, HTMLReactParserOptions, domToReact } from "html-react-parser";
import { icons, LucideIcon } from "lucide-react";
import Image, { ImageLoaderProps } from "next/image";
import Link from "next/link";
import { Fragment, ReactNode, createElement, memo, useCallback, useMemo } from "react";
import { useWidthBudget, WidthBudget } from "./widthBudget";

type ViewportName = "mobile" | "tablet" | "desktop";
type BreakpointName = "xs" | "sm2" | "lg";
type CellAlign = "start" | "center" | "end";

type Visibility = Record<ViewportName, boolean>;

type TableViewportSettings = {
    visible: boolean;
    font_size: string;
    cell_padding_y: string;
    cell_padding_x: string;
};

type CellBase = {
    link?: string | null;
    align?: CellAlign;
};

type EmptyCell = CellBase & { type: "empty" };
type TextCell = CellBase & {
    type: "text";
    text: string;
    note?: string | null;
    bold?: boolean;
    color?: string | null;
};
type RichCell = CellBase & { type: "rich"; html: string };
type IconCell = CellBase & {
    type: "icon";
    icon: string;
    color?: string | null;
    text?: string | null;
    // The same quieter second line a text cell has, so a row of verdicts can carry its
    // detail without changing shape.
    note?: string | null;
};
type BadgeCell = CellBase & {
    type: "badge";
    text: string;
    color?: string | null;
    background_color?: string | null;
};
type ImageCell = CellBase & {
    type: "image";
    media_id: string | null;
    alt?: string | null;
    ratio?: string | null;
    object_fit?: "cover" | "contain";
};
type CardCell = CellBase & {
    type: "card";
    media_id?: string | null;
    alt?: string | null;
    ratio?: string | null;
    icon?: string | null;
    icon_color?: string | null;
    // A colour swatch shown in place of the icon: any CSS background value, so a flat
    // hex for a solid tint and a gradient for a lens that is two-tone.
    swatch?: string | null;
    // "inline" keeps the marker next to the title as one group; "start" pins it to the
    // start edge so the title can centre on the card.
    marker_position?: "inline" | "start" | null;
    title: string;
    subtitle?: string | null;
};
type LinkCell = CellBase & { type: "link"; text: string; url: string };

type TableCell = EmptyCell | TextCell | RichCell | IconCell | BadgeCell | ImageCell | CardCell | LinkCell;

type TableColumn = {
    id: number;
    kind: "label" | "data";
    width: string | null;
    min_width: string;
    align: CellAlign;
    highlight: boolean;
    visible: Visibility;
    header: TableCell;
};

type TableRow = {
    id: number;
    visible: Visibility;
    label: TableCell | null;
    cells: TableCell[];
};

type TableSettings = {
    label: string | null;
    anchor_id?: string | null;
    full_width: boolean;
    background_color: string | null;
    border_color: string | null;
    border_radius: string;
    header_variant: "card" | "text" | "none";
    sticky_first_column: boolean;
    zebra: boolean;
    row_hover: boolean;
    mobile: TableViewportSettings;
    tablet: TableViewportSettings;
    desktop: TableViewportSettings;
};

type TableData = {
    columns: TableColumn[];
    rows: TableRow[];
};

type ResponsiveString = Record<BreakpointName, string>;

// A resolved column width: either a fixed gutter, or a percentage of the table once the
// gutters are taken out. `fixedTotal` travels with it so the CSS can say exactly that.
type ColumnShare = { px: number } | { percent: number; fixedTotal?: number };

const VIEWPORT_BREAKPOINT: Record<ViewportName, BreakpointName> = {
    mobile: "xs",
    tablet: "sm2",
    desktop: "lg",
};

const BREAKPOINT_VIEWPORT: Record<BreakpointName, ViewportName> = {
    xs: "mobile",
    sm2: "tablet",
    lg: "desktop",
};

const DEFAULT_VIEWPORT: TableViewportSettings = {
    visible: true,
    font_size: "14",
    cell_padding_y: "12",
    cell_padding_x: "16",
};

const ALLOWED_RICH_TAGS = new Set(["p", "strong", "em", "u", "s", "ul", "ol", "li", "br", "a", "span"]);
const iconMap = Object.fromEntries(Object.entries(icons).map(([name, Icon]) => [toKebabCase(name), Icon])) as Partial<Record<string, LucideIcon>>;
const EMPTY_COLUMNS: TableColumn[] = [];
const EMPTY_ROWS: TableRow[] = [];
// Opaque equivalents of the Quill table row colours over a white surface.
const ZEBRA_SURFACE = "#FAFBFD";
const HOVER_SURFACE = "#F3F9FF";
// The tint behind a highlighted column. Neutral on purpose: the brand red reads as a
// warning next to the green and amber icons a comparison table uses.
const HIGHLIGHT_SURFACE = "#F2F4F7";
const SWATCH_SIZE = 36;

const TableLC = memo(function TableLC({ content, disableLinks }: {
    content: { settings: TableSettings; data: TableData };
    disableLinks?: boolean;
}) {
    const settings = content?.settings;
    const columns = content?.data?.columns ?? EMPTY_COLUMNS;
    const rows = content?.data?.rows ?? EMPTY_ROWS;
    const widthBudget = useWidthBudget();

    const layout = useMemo(() => buildResponsiveLayout(columns, widthBudget), [columns, widthBudget]);
    const dataColumns = useMemo(() => {
        let dataIndex = 0;
        return columns.map(column => ({
            column,
            dataIndex: column.kind === "data" ? dataIndex++ : null,
        }));
    }, [columns]);
    const firstVisibleRowIndex = useMemo(() => responsive(viewport => (
        rows.findIndex(row => row.visible?.[viewport] !== false)
    )), [rows]);
    const lastVisibleRowIndex = useMemo(() => responsive(viewport => {
        for (let index = rows.length - 1; index >= 0; index -= 1) {
            if (rows[index].visible?.[viewport] !== false) return index;
        }
        return -1;
    }), [rows]);

    if (!settings || !columns.length) return null;

    const viewports = {
        mobile: { ...DEFAULT_VIEWPORT, ...settings.mobile },
        tablet: { ...DEFAULT_VIEWPORT, ...settings.tablet },
        desktop: { ...DEFAULT_VIEWPORT, ...settings.desktop },
    };
    const visible = responsive(viewport => viewports[viewport].visible ? "block" : "none");
    const fontSize = responsive(viewport => `${toNumber(viewports[viewport].font_size, 14)}px`);
    const paddingY = responsive(viewport => `${toNumber(viewports[viewport].cell_padding_y, 12)}px`);
    const paddingX = responsive(viewport => `${toNumber(viewports[viewport].cell_padding_x, 16)}px`);
    const borderColor = settings.border_color || "#EEEEEE";
    const surface = settings.background_color || "#FFFFFF";
    const radius = `${toNumber(settings.border_radius, 0)}px`;
    const hasStickyLabel = settings.sticky_first_column && columns[0]?.kind === "label";

    return (
        <Box sx={{ display: visible, width: 1, bgcolor: surface, py: { xs: 1.5, sm2: 2, lg: 2.5 } }}>
            <Box
                sx={{
                    width: 1,
                    maxWidth: settings.full_width ? "none" : 1400,
                    mx: "auto",
                    paddingInlineStart: settings.full_width ? 0 : { xs: 2, sm2: 2, lg2: 0 },
                    paddingInlineEnd: settings.full_width ? 0 : { xs: 0, sm2: 2, lg2: 0 },
                }}
            >
                <Box
                    data-table-scroller
                    sx={{
                        display: "block",
                        width: 1,
                        overflowX: "auto",
                        WebkitOverflowScrolling: "touch",
                        direction: "ltr",
                    }}
                >
                    <Box
                        component="table"
                        sx={{
                            width: "100%",
                            minWidth: layout.tableMinWidth,
                            tableLayout: "fixed",
                            borderCollapse: "separate",
                            borderSpacing: 0,
                            overflow: "clip",
                            fontSize,
                            color: "#171717",
                            bgcolor: "#FFFFFF",
                            border: 0,
                            borderBlockStart: `1px solid ${borderColor}`,
                            borderBlockEnd: `1px solid ${borderColor}`,
                            borderInlineEnd: `1px solid ${borderColor}`,
                            borderInlineStart: hasStickyLabel ? 0 : `1px solid ${borderColor}`,
                            borderRadius: radius,
                        }}
                    >
                        <colgroup>
                            {columns.map((column, index) => (
                                <Box
                                    component="col"
                                    key={column.id}
                                    sx={{
                                        display: visibilityDisplay(column.visible, "table-column"),
                                        width: responsive(viewport => shareToCss(layout.shares[VIEWPORT_BREAKPOINT[viewport]][index])),
                                    }}
                                />
                            ))}
                        </colgroup>

                        {settings.header_variant !== "none" && (
                            <thead>
                                <tr>
                                    {columns.map((column, index) => (
                                        <TableHeaderCell
                                            key={column.id}
                                            column={column}
                                            index={index}
                                            settings={settings}
                                            layout={layout}
                                            paddingX={paddingX}
                                            paddingY={paddingY}
                                            borderColor={borderColor}
                                            surface={surface}
                                            disableLinks={disableLinks}
                                        />
                                    ))}
                                </tr>
                            </thead>
                        )}

                        <tbody>
                            {rows.map((row, rowIndex) => {
                                const rowSurface = settings.zebra && rowIndex % 2 ? ZEBRA_SURFACE : "#FFFFFF";
                                const borderBlockEnd = responsive(viewport => (
                                    lastVisibleRowIndex[VIEWPORT_BREAKPOINT[viewport]] === rowIndex
                                        ? "none"
                                        : `1px solid ${borderColor}`
                                ));
                                const borderStartStartRadius = responsive(viewport => (
                                    settings.header_variant === "none"
                                    && firstVisibleRowIndex[VIEWPORT_BREAKPOINT[viewport]] === rowIndex
                                        ? radius
                                        : "0px"
                                ));
                                const borderEndStartRadius = responsive(viewport => (
                                    lastVisibleRowIndex[VIEWPORT_BREAKPOINT[viewport]] === rowIndex
                                        ? radius
                                        : "0px"
                                ));
                                return (
                                    <Box
                                        component="tr"
                                        key={row.id}
                                        sx={{
                                            display: visibilityDisplay(row.visible, "table-row"),
                                            ...(settings.row_hover && { "&:hover > th, &:hover > td": { bgcolor: HOVER_SURFACE } }),
                                        }}
                                    >
                                        {dataColumns.map(({ column, dataIndex }, columnIndex) => {
                                            const isLabel = column.kind === "label" && columnIndex === 0;
                                            const cell = isLabel
                                                ? row.label ?? EMPTY_CELL
                                                : dataIndex === null ? EMPTY_CELL : row.cells?.[dataIndex] ?? EMPTY_CELL;
                                            return (
                                                <TableBodyCell
                                                    key={column.id}
                                                    cell={cell}
                                                    column={column}
                                                    columnIndex={columnIndex}
                                                    rowSurface={rowSurface}
                                                    isLabel={isLabel}
                                                    sticky={isLabel && settings.sticky_first_column}
                                                    layout={layout}
                                                    paddingX={paddingX}
                                                    paddingY={paddingY}
                                                    borderColor={borderColor}
                                                    borderBlockEnd={borderBlockEnd}
                                                    borderStartStartRadius={borderStartStartRadius}
                                                    borderEndStartRadius={borderEndStartRadius}
                                                    disableLinks={disableLinks}
                                                />
                                            );
                                        })}
                                    </Box>
                                );
                            })}
                        </tbody>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
});

export default TableLC;

const EMPTY_CELL: EmptyCell = { type: "empty" };

function TableHeaderCell({
    column,
    index,
    settings,
    layout,
    paddingX,
    paddingY,
    borderColor,
    surface,
    disableLinks,
}: {
    column: TableColumn;
    index: number;
    settings: TableSettings;
    layout: ReturnType<typeof buildResponsiveLayout>;
    paddingX: ResponsiveString;
    paddingY: ResponsiveString;
    borderColor: string;
    surface: string;
    disableLinks?: boolean;
}) {
    const cardVariant = settings.header_variant === "card";
    const emptyLabelHeader = index === 0 && column.kind === "label" && column.header?.type === "empty";
    const sticky = settings.sticky_first_column && index === 0 && column.kind === "label";

    return (
        <Box
            component="th"
            scope="col"
            sx={{
                display: columnDisplay(column),
                minWidth: columnMinWidth(column),
                p: cardVariant ? 0 : undefined,
                px: cardVariant ? 0 : paddingX,
                py: cardVariant ? 0 : paddingY,
                textAlign: column.align,
                verticalAlign: cardVariant ? "top" : "middle",
                fontWeight: settings.header_variant === "text" ? 700 : 400,
                bgcolor: emptyLabelHeader ? surface : column.highlight ? HIGHLIGHT_SURFACE : cardVariant ? "#FFFFFF" : "#F5F5F5",
                borderBlockEnd: `1px solid ${borderColor}`,
                borderInlineEnd: index < layout.columnCount - 1 ? `1px solid ${borderColor}` : 0,
                ...(sticky && {
                    position: "sticky",
                    insetInlineStart: 0,
                    zIndex: 4,
                    borderInlineStart: `1px solid ${borderColor}`,
                    borderStartStartRadius: `${toNumber(settings.border_radius, 0)}px`,
                }),
            }}
        >
            <CellContent cell={column.header ?? EMPTY_CELL} column={column} columnIndex={index} layout={layout} disableLinks={disableLinks} />
        </Box>
    );
}

function TableBodyCell({
    cell,
    column,
    columnIndex,
    rowSurface,
    isLabel,
    sticky,
    layout,
    paddingX,
    paddingY,
    borderColor,
    borderBlockEnd,
    borderStartStartRadius,
    borderEndStartRadius,
    disableLinks,
}: {
    cell: TableCell;
    column: TableColumn;
    columnIndex: number;
    rowSurface: string;
    isLabel: boolean;
    sticky: boolean;
    layout: ReturnType<typeof buildResponsiveLayout>;
    paddingX: ResponsiveString;
    paddingY: ResponsiveString;
    borderColor: string;
    borderBlockEnd: ResponsiveString;
    borderStartStartRadius: ResponsiveString;
    borderEndStartRadius: ResponsiveString;
    disableLinks?: boolean;
}) {
    const element = isLabel ? "th" : "td";
    const background = column.highlight ? HIGHLIGHT_SURFACE : rowSurface;

    return (
        <Box
            component={element}
            {...(isLabel ? { scope: "row" } : {})}
            sx={{
                display: columnDisplay(column),
                minWidth: columnMinWidth(column),
                px: paddingX,
                py: paddingY,
                textAlign: cell.align ?? column.align,
                verticalAlign: "middle",
                bgcolor: background,
                borderBlockEnd,
                borderInlineEnd: columnIndex < layout.columnCount - 1 ? `1px solid ${borderColor}` : 0,
                transition: "background-color 150ms ease",
                ...(sticky && {
                    position: "sticky",
                    insetInlineStart: 0,
                    zIndex: 3,
                    fontWeight: 400,
                    borderInlineStart: `1px solid ${borderColor}`,
                    borderStartStartRadius,
                    borderEndStartRadius,
                }),
            }}
        >
            <CellContent cell={cell} column={column} columnIndex={columnIndex} layout={layout} disableLinks={disableLinks} />
        </Box>
    );
}

function CellContent({ cell, column, columnIndex, layout, disableLinks }: {
    cell: TableCell;
    column: TableColumn;
    columnIndex: number;
    layout: ReturnType<typeof buildResponsiveLayout>;
    disableLinks?: boolean;
}) {
    const content = renderCell(cell, column, columnIndex, layout, disableLinks);
    const href = cell.type === "link" ? cell.url : cell.link;
    if (!href || disableLinks) return content;

    return <Link href={href} style={{ display: "block", color: "inherit", textDecoration: "none" }}>{content}</Link>;
}

function renderCell(cell: TableCell, column: TableColumn, columnIndex: number, layout: ReturnType<typeof buildResponsiveLayout>, disableLinks?: boolean): ReactNode {
    const alignment = cell.align ?? column.align;

    switch (cell.type) {
        case "empty":
            return null;
        case "text":
            return (
                <Box>
                    <Typography component="span" sx={{ display: "block", color: cell.color || "inherit", font: "inherit", fontWeight: cell.bold ? 700 : 400 }}>
                        {cell.text}
                    </Typography>
                    {cell.note && <Typography component="span" sx={{ display: "block", mt: 0.5, color: "#777777", fontSize: "0.86em", lineHeight: 1.6 }}>{cell.note}</Typography>}
                </Box>
            );
        case "rich":
            return (
                <Box sx={{ "& p": { m: 0 }, "& ul, & ol": { my: 0.5, ps: 2.5 }, "& a": { color: "primary.main" } }}>
                    {renderSafeRichHtml(cell.html, !!disableLinks)}
                </Box>
            );
        case "icon": {
            const Icon = iconMap[cell.icon];
            return (
                <Box
                    sx={{
                        display: "inline-flex",
                        flexDirection: "column",
                        // The note is its own line, so the verdict and its icon stay one
                        // group and both lines follow the cell's alignment.
                        alignItems: alignment === "center" ? "center" : alignment === "end" ? "flex-end" : "flex-start",
                        gap: 0.25,
                        color: cell.color || "inherit",
                    }}
                >
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                        {/* The colour has to go on the icon itself. A global .lucide rule sets a
                            colour on the svg, which beats inheritance, so the stroke's
                            currentColor would resolve to that grey and not to this cell's. */}
                        {Icon && <Icon size={18} strokeWidth={2} color={cell.color || undefined} aria-hidden="true" />}
                        {cell.text && <Box component="span" sx={{ fontWeight: cell.note ? 700 : 400 }}>{cell.text}</Box>}
                    </Box>
                    {cell.note && (
                        <Box component="span" sx={{ fontSize: 12, lineHeight: 1.5, fontWeight: 400, color: "#777777", textAlign: alignment }}>{cell.note}</Box>
                    )}
                </Box>
            );
        }
        case "badge":
            return (
                <Box component="span" sx={{ display: "inline-flex", px: 1.25, py: 0.5, borderRadius: "999px", color: cell.color || "#171717", bgcolor: cell.background_color || "#F5F5F5", fontSize: "0.9em", fontWeight: 600 }}>
                    {cell.text}
                </Box>
            );
        case "image":
            return cell.media_id ? <CellImage cell={cell} column={column} columnIndex={columnIndex} layout={layout} /> : null;
        case "card": {
            const Icon = cell.icon ? iconMap[cell.icon] : null;
            // "start" lifts the marker out of the flow and pins it to the start edge, so the
            // title can centre against the whole card rather than against what is left over.
            const pinnedMarker = cell.marker_position === "start";
            const markerGutter = `${SWATCH_SIZE + 8}px`;
            return (
                <Box sx={{ width: 1, overflow: "hidden", borderRadius: "8px", bgcolor: "#FFFFFF" }}>
                    {cell.media_id && (
                        <CellImage
                            cell={{
                                type: "image",
                                media_id: cell.media_id,
                                alt: cell.alt,
                                ratio: cell.ratio,
                                object_fit: "cover",
                            }}
                            column={column}
                            columnIndex={columnIndex}
                            layout={layout}
                        />
                    )}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: alignment === "center" ? "center" : alignment === "end" ? "flex-end" : "flex-start",
                            gap: 1,
                            // 16px on every side, so the marker keeps an equal gap from the
                            // top, bottom and start edge of the strip.
                            p: 2,
                            ...(pinnedMarker && { position: "relative" }),
                            borderBlockStart: cell.media_id ? "1px solid #EEEEEE" : 0,
                        }}
                    >
                        {cell.swatch ? (
                            <Box
                                aria-hidden="true"
                                sx={{
                                    flexShrink: 0,
                                    ...(pinnedMarker && {
                                        position: "absolute",
                                        insetInlineStart: 16,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                    }),
                                    // Fixed rather than stretched to the strip: a stretched flex
                                    // item cannot resolve a width from aspect-ratio and collapses
                                    // to zero.
                                    width: SWATCH_SIZE,
                                    height: SWATCH_SIZE,
                                    borderRadius: "50%",
                                    background: cell.swatch,
                                    // A pale swatch would otherwise disappear into the white card.
                                    boxShadow: "inset 0 0 0 1px rgba(23, 23, 23, 0.12)",
                                }}
                            />
                        ) : Icon ? (
                            <Box
                                component="span"
                                sx={{
                                    display: "inline-flex",
                                    ...(pinnedMarker && {
                                        position: "absolute",
                                        insetInlineStart: 16,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                    }),
                                }}
                            >
                                <Icon size={20} color={cell.icon_color || "#BB202E"} strokeWidth={2} aria-hidden="true" />
                            </Box>
                        ) : null}
                        <Box
                            sx={{
                                minWidth: 0,
                                textAlign: alignment,
                                // The gutter is mirrored on both sides so the title stays centred
                                // on the card and can never run under the pinned marker.
                                ...(pinnedMarker && { flex: 1, paddingInline: markerGutter }),
                            }}
                        >
                            <Typography component="span" sx={{ display: "block", color: "#171717", fontSize: 14, lineHeight: 1.5, fontWeight: 700 }}>{cell.title}</Typography>
                            {cell.subtitle && <Typography component="span" sx={{ display: "block", mt: 0.25, color: "#777777", fontSize: 12, lineHeight: 1.5 }}>{cell.subtitle}</Typography>}
                        </Box>
                    </Box>
                </Box>
            );
        }
        case "link":
            return <Box component="span" sx={{ color: "primary.main", textDecoration: disableLinks ? "none" : "underline", textUnderlineOffset: "3px" }}>{cell.text}</Box>;
        default:
            return null;
    }
}

function CellImage({ cell, column, columnIndex, layout }: {
    cell: ImageCell;
    column: TableColumn;
    columnIndex: number;
    layout: ReturnType<typeof buildResponsiveLayout>;
}) {
    const loader = useCallback(({ width }: ImageLoaderProps) => generateMediaSrc(cell.media_id ?? "", {
        width: Math.min(width, 1920),
        quality: 88,
        action: cell.object_fit === "contain" ? "thumbnail" : "cropThumbnail",
    }), [cell.media_id, cell.object_fit]);
    const src = useMemo(() => generateMediaSrc(cell.media_id ?? ""), [cell.media_id]);
    const sizes = useMemo(() => imageSizes(column, columnIndex, layout), [column, columnIndex, layout]);
    const ratio = cell.ratio && cell.ratio !== "unset" ? cell.ratio : "16 / 9";

    return (
        <Box sx={{ position: "relative", width: 1, aspectRatio: ratio, overflow: "hidden", bgcolor: "#F5F5F5" }}>
            <Image
                src={src}
                loader={loader}
                sizes={sizes}
                alt={cell.alt || ""}
                fill
                loading="lazy"
                fetchPriority="low"
                style={{ objectFit: cell.object_fit || "cover" }}
            />
        </Box>
    );
}

function shareToCss(share: ColumnShare | undefined) {
    if (!share) return "0%";
    if ("px" in share) return `${share.px}px`;
    if (!share.fixedTotal) return `${share.percent}%`;
    return `calc((100% - ${share.fixedTotal}px) * ${share.percent / 100})`;
}

function shareToPixels(share: ColumnShare | undefined, tableWidth: number) {
    if (!share) return 0;
    if ("px" in share) return share.px;
    return Math.max(0, tableWidth - (share.fixedTotal ?? 0)) * share.percent / 100;
}

function buildResponsiveLayout(columns: TableColumn[], budget: WidthBudget) {
    const shares = { xs: [] as ColumnShare[], sm2: [] as ColumnShare[], lg: [] as ColumnShare[] };
    const tableWidth = { xs: 0, sm2: 0, lg: 0 };
    const tableMinWidth = { xs: "0px", sm2: "0px", lg: "0px" };

    (Object.keys(BREAKPOINT_VIEWPORT) as BreakpointName[]).forEach(breakpoint => {
        const viewport = BREAKPOINT_VIEWPORT[breakpoint];
        const visibleIndexes = columns.map((column, index) => column.visible?.[viewport] !== false ? index : -1).filter(index => index >= 0);
        const visibleColumns = visibleIndexes.map(index => columns[index]);
        const resolved = distributeWidths(visibleColumns);
        const minWidth = visibleColumns.reduce((sum, column) => sum + toNumber(column.min_width, 0), 0);
        const naturalWidth = Math.max(budget[breakpoint], minWidth);

        shares[breakpoint] = columns.map(() => ({ percent: 0 }) as ColumnShare);
        visibleIndexes.forEach((columnIndex, visibleIndex) => {
            shares[breakpoint][columnIndex] = resolved[visibleIndex];
        });
        tableWidth[breakpoint] = naturalWidth;
        tableMinWidth[breakpoint] = `${minWidth}px`;
    });

    return { shares, tableWidth, tableMinWidth, columnCount: columns.length };
}

// A column width is either a fixed pixel gutter ("124px"), a percentage of what is
// left after those gutters ("25"), or null to share that remainder equally. A row-label
// column has to be fixed: a percentage of a table that shrinks shrinks with it, and the
// labels start wrapping long before the table reaches its minimum width.
function parseColumnWidth(width: string | null): { px: number } | { percent: number } | null {
    if (width === null || width === "") return null;
    const pixels = /^\s*(\d+(?:\.\d+)?)px\s*$/.exec(width);
    if (pixels) return { px: Number(pixels[1]) };
    const percent = Number(width);
    return Number.isFinite(percent) && percent >= 0 ? { percent } : null;
}

function distributeWidths(columns: TableColumn[]) {
    if (!columns.length) return [];

    const parsed = columns.map(column => parseColumnWidth(column.width));
    const fixedTotal = parsed.reduce<number>((total, width) => total + (width && "px" in width ? width.px : 0), 0);
    const declared = parsed.map(width => (width !== null && "percent" in width ? width.percent : null));
    // Fixed columns are out of the percentage pool entirely; the flexible ones then
    // share 100% of whatever is left, which `calc()` resolves at any table width.
    const flexibleIndexes = parsed.map((width, index) => (width && "px" in width ? -1 : index)).filter(index => index >= 0);
    const flexible = flexibleIndexes.map(index => declared[index]);
    const sum = flexible.reduce<number>((total, width) => total + (width ?? 0), 0);
    const nullCount = flexible.filter(width => width === null).length;
    const invalid = sum > 100 || (sum === 100 && nullCount > 0);
    const useEqual = !flexible.length || invalid || (!nullCount && sum !== 100);

    let percents: number[];
    if (useEqual) {
        percents = equalShares(flexible.length);
    } else if (!nullCount) {
        percents = normalizeRounding(flexible as number[]);
    } else {
        const remainderShare = (100 - sum) / nullCount;
        percents = normalizeRounding(flexible.map(width => width ?? remainderShare), flexible.lastIndexOf(null));
    }

    const shares: ColumnShare[] = parsed.map(width => (width && "px" in width ? { px: width.px } : { percent: 0 }));
    flexibleIndexes.forEach((columnIndex, position) => {
        shares[columnIndex] = { percent: percents[position] ?? 0 };
    });
    return shares.map(share => ("px" in share ? share : { ...share, fixedTotal }));
}

function equalShares(count: number) {
    return normalizeRounding(Array.from({ length: count }, () => 100 / count));
}

function normalizeRounding(values: number[], remainderIndex = values.length - 1) {
    const rounded = values.map(value => Math.round(value * 100) / 100);
    const total = rounded.reduce((sum, value) => sum + value, 0);
    if (rounded.length) rounded[remainderIndex] = Math.round((rounded[remainderIndex] + 100 - total) * 100) / 100;
    return rounded;
}

function imageSizes(column: TableColumn, columnIndex: number, layout: ReturnType<typeof buildResponsiveLayout>) {
    const widthFor = (breakpoint: BreakpointName) => {
        const share = layout.shares[breakpoint][columnIndex];
        return Math.max(toNumber(column.min_width, 0), Math.round(shareToPixels(share, layout.tableWidth[breakpoint])));
    };
    return `(max-width: 767px) ${widthFor("xs")}px, (max-width: 1199px) ${widthFor("sm2")}px, ${widthFor("lg")}px`;
}

function renderSafeRichHtml(html: string, disableLinks: boolean) {
    const options: HTMLReactParserOptions = {
        replace(node: DOMNode) {
            if (!(node instanceof HtmlElement)) return undefined;
            const tag = node.name.toLowerCase();
            const children = domToReact(node.children as DOMNode[], options);
            if (!ALLOWED_RICH_TAGS.has(tag)) return <Fragment>{children}</Fragment>;

            if (tag === "a") {
                const href = node.attribs?.href;
                if (disableLinks || !isSafeHref(href)) return <span>{children}</span>;
                return <a href={href}>{children}</a>;
            }

            return createElement(tag, null, children);
        },
    };

    return parse(html || "", options);
}

function isSafeHref(href?: string) {
    return !!href && (/^\/fa\//.test(href) || /^https?:\/\//i.test(href));
}

function columnDisplay(column: TableColumn) {
    return visibilityDisplay(column.visible, "table-cell");
}

function visibilityDisplay(visible: Visibility | undefined, display: string) {
    return responsive(viewport => visible?.[viewport] !== false ? display : "none");
}

function columnMinWidth(column: TableColumn) {
    const value = `${toNumber(column.min_width, 0)}px`;
    return responsive(viewport => column.visible?.[viewport] !== false ? value : "0px");
}

function responsive<T>(pick: (viewport: ViewportName) => T): Record<BreakpointName, T> {
    return {
        xs: pick("mobile"),
        sm2: pick("tablet"),
        lg: pick("desktop"),
    };
}

function toNumber(value: string | number | null | undefined, fallback: number) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
}

function toKebabCase(value: string) {
    return value
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2")
        .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
        .toLowerCase();
}
